import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  alpha,
  Avatar,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { amberColors, numenorColors } from '../../theme/themeConfig';

// Icons
import HomeIcon from '@mui/icons-material/Home';

export const FoundersStatement: React.FC = () => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const navigate = useNavigate();

  // Background style consistent with other pages
  const backgroundStyle = darkMode
    ? {
        background: `
          linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }
    : {
        background: `
          linear-gradient(135deg, ${alpha(numenorColors[50], 0.78)} 0%, ${alpha(numenorColors[100], 0.78)} 50%, ${alpha(numenorColors[50], 0.78)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      };

  return (
    <>
      <Helmet>
        <title>Founder's Statement | Unity Build Estimation</title>
        <meta
          name="description"
          content="Meet Phil, founder of Unity Build Estimation and learn about the vision and passion behind this construction estimation platform."
        />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          ...backgroundStyle,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 2, sm: 3, md: 6 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* Home Icon - Top Right */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16, md: 24 },
              right: { xs: 12, sm: 16, md: 24 },
              zIndex: 1000,
            }}
          >
            <Tooltip title="Back to Home" arrow>
              <IconButton
                onClick={() => navigate('/')}
                sx={{
                  bgcolor: alpha(darkMode ? '#1a1a1a' : '#ffffff', 0.9),
                  backdropFilter: 'blur(10px)',
                  border: 1,
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: amberColors[600],
                  minWidth: { xs: 40, sm: 44, md: 48 },
                  minHeight: { xs: 40, sm: 44, md: 48 },
                  '&:hover': {
                    bgcolor: alpha(darkMode ? '#2d2d2d' : '#f5f5f5', 0.95),
                    borderColor: amberColors[600],
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <HomeIcon fontSize={window.innerWidth < 600 ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Main Content Paper */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3, md: 6 },
              backgroundColor: darkMode
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha(theme.palette.background.paper, 0.98),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: 1,
              borderColor: alpha(theme.palette.divider, 0.2),
            }}
          >
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {/* Photo and Basic Info */}
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 4 } }}>
                  {/* Placeholder for founder photo */}
                  <Avatar
                    src="/images/founder.png"
                    alt="Phil - Founder of Unity Build Estimation"
                    sx={{
                      width: { xs: 80, sm: 100, md: 160 },
                      height: { xs: 80, sm: 100, md: 160 },
                      mx: 'auto',
                      mb: { xs: 2, md: 3 },
                      border: `3px solid ${amberColors[600]}`,
                      boxShadow: `0 4px 12px ${alpha(amberColors[600], 0.3)}`,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                    }}
                  >
                    Phil
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    }}
                  >
                    Founder & Lead Developer
                  </Typography>
                </Box>
              </Grid>

              {/* Main Content */}
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 2, sm: 3, md: 4 },
                    color: amberColors[600],
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
                  }}
                >
                  A Personal Message
                </Typography>

                <Box sx={{ mb: { xs: 2, md: 4 } }}>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    I'm an electrician by trade, tinkerer by nature, and a guy who can't look at a
                    workflow without wanting to shave five steps off it. I began to learn the
                    electrical trade in 1994 at a vocational-tech school, joined the IBEW right out
                    of high school, and I've since worn many hats: apprentice, master, installer,
                    foreman, superintendent, department director, business owner, and now a bit of
                    all of those things, plus a software developer.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    In 2022, when ChatGPT hit the world, I was blown away by the power of this
                    technology. As I was asking the program to write a poem about a dog riding a
                    skateboard in the style of Shakespeare, it became obvious to me that this new
                    tool would change the world. That it will change everything about the way we
                    work.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    So, I decided to learn how to code. I've gotta say, it's been so much fun!
                    Thanks to Artificial Intelligence, it barely feels like work! I thought, "what
                    if I could bring these tools into my work environment?" It's clear that AI has a
                    direct positive impact on productivity and we, therefore, have to incorporate it
                    into our workflows. I did not want to wait for the tried-and-true estimation
                    services to build the system that THEY feel I need. Plus, there's no doubt I
                    would have to pay through the nose to use it.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    Now, we have Unity Build Estimation. Phase 1 of development is complete: Core
                    Features Needed for Estimation Software. We are quickly moving into Phase 2:
                    Integrating AI Technology Into Workflows, starting with our full-featured items
                    and assemblies database (coming by late Q3 / early Q4).
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    As a new product on the market, I know I need to earn your trust. What I can
                    promise you is this: The project is my obsession. I will continue to refine and
                    perfect it for many years to come. If you aren't ready or if the product doesn't
                    seem to offer what you need, please let me know. I also encourage you to check
                    in from time to time to see what's new.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: { xs: 1.5, md: 2 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    Stay safe, work smart,
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: { xs: 1.5, md: 2 },
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                    }}
                  >
                    Phil
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                    }}
                  >
                    Built by a tradesman who happens to write code.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: alpha(amberColors[600], 0.1),
                    borderRadius: 2,
                    p: { xs: 2, sm: 2.5, md: 3 },
                    border: 1,
                    borderColor: alpha(amberColors[600], 0.2),
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    Reach me directly at phil@unitybuildestimation.com
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default FoundersStatement;
